import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import FormularioCursos from './FormularioCursos';
import "../stylescss/carrusel.css";

const slides = [
    {
    id: 1,
    tag: "Nuestra Trayectoria",
    title: "Educación ejecutiva  desde el 2003",
    body: "La Escuela de Gerencia y Gestión impulsa, desde el año 2003, la educación ejecutiva dirigida a profesionales y líderes que generan cambios en el ámbito público y empresarial. A través del desarrollo del liderazgo, promovemos la transformación organizacional y fomentamos la innovación para construir el futuro.",
    image: "/uploads/port.jpg",
    imageAlt: "Líderes en reunión estratégica",
    accent: "#67d6a4",
    },
    {
    id: 2,
    tag: "Capacitación  Continua",
    title: "Impulsa tu desarrollo profesional",
    body: "Promovemos la capacitacion  profesional a través de cursos, capacitación y consultoría, orientadas a fortalecer la gestión en el sector público y empresarial en áreas como dirección, talento humano, finanzas, marketing, operaciones, gestión legal, tecnologías de la información y mejora continua.",
    image: "/uploads/gestion-pu.jpg",
    imageAlt: "Capacitación profesional",
    accent: "#67d6a4",
    },
    {
    id: 3,
    tag: "Programas Especializados",
    title: "Programas de prácticas de alto impacto",
    body: "Nuestros Programas Especializados en Gestión Pública y Gestión Empresarial constituyen los pilares de nuestra oferta académica, diseñados para fortalecer conocimientos, desarrollar habilidades prácticas y responder a las demandas actuales de las organizaciones. A través de estas iniciativas brindamos educación ejecutiva, capacitación y fortalecimiento actualizada y de alto impacto mediante:",
    image: "/uploads/curs_proyecto_bim.png",
    imageAlt: "Capacitación profesional",
    accent: "#67d6a4",
    },
    {
    id: 4,
    tag: "Capacitación Corporativa",
    title: "Cursos a la medida para tu organización",
    body: "Diseñamos programas de capacitación personalizados para atender las necesidades específicas del sector público y del sector empresarial. Desarrollamos el Plan de Desarrollo de Personas (PDP) para instituciones del Estado y ofrecemos cursos cerrados para empresas y organizaciones, orientados a fortalecer competencias en sistemas administrativos y tecnologías de la información (TIC), optimizando así la inversión en capacitación y el desarrollo del talento humano.",
    image: "/uploads/curs_recursos_humanos.jpg",
    imageAlt: "Capacitación profesional",
    accent: "#67d6a4",
    }
];

const DURATION = 5000;

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState("next");
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  
  // Estados para hacer movible el formulario
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const offsetRef = useRef({ x: 0, y: 0 });
  const formRef = useRef(null);

  const carouselRef = useRef(null);

  const goTo = useCallback((index, dir = "next") => {
    if (animating) return;
    setDirection(dir);
    setAnimating(true);
    setProgress(0);

    setTimeout(() => {
      setCurrent(index);
      setAnimating(false);
    }, 500);
  }, [animating]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length, "next");
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length, "prev");
  }, [current, goTo]);

  useEffect(() => {
    if (paused) return;

    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          next();
          return 0;
        }
        return p + 100 / (DURATION / 100);
      });
    }, 100);

    return () => clearInterval(interval);
  }, [paused, next]);

  // Lógica para arrastrar el formulario con el mouse
  const handleMouseDown = (e) => {
    if (['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA', 'LABEL', 'OPTION'].includes(e.target.tagName)) return;
    
    setIsDragging(true);
    offsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;

      let newX = e.clientX - offsetRef.current.x;
      let newY = e.clientY - offsetRef.current.y;

      // Medimos el tamaño real del formulario
      const formWidth = formRef.current ? formRef.current.offsetWidth : 400;
      const formHeight = formRef.current ? formRef.current.offsetHeight : 500;

      // Posición inicial basada en tu CSS (right: 60px, top: 225px)
      const initialLeft = window.innerWidth - 60 - formWidth;

      // Detectamos si estamos arriba en el carrusel
      const isAtHero = window.scrollY < 300; 

      // Si está arriba en el carrusel, bloquea para no entrar al área roja.
      // Si hace scroll hacia abajo, minX se vuelve -initialLeft dándole libertad total por toda la pantalla.
      const minX = isAtHero ? -initialLeft + 720 : -initialLeft + 24; 
      const maxX = 60;                                    
      const minY = -73;                                  
      const maxY = window.innerHeight - formHeight - 225; 

      if (newX < minX) newX = minX;
      if (newX > maxX) newX = maxX;
      if (newY < minY) newY = minY;
      if (newY > maxY) newY = maxY;

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);
  
  const slide = slides[current];

  return (
    <div
      ref={carouselRef}
      className="carousel-root"
      style={{ "--accent": slide.accent }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="carousel-progress" style={{ width: `${progress}%` }} />

      <div className="carousel-counter">
        <span>{current + 1}</span> / {slides.length}
      </div>
      <div
        className={`carousel-track ${
          animating ? `animating-${direction}` : ""
        }`}
      >
        <div className="carousel-slide">
          <div className="carousel-content-col">
            <div className="carousel-tag"><h1>{slide.tag}</h1></div>
            <h2 className="carousel-title">{slide.title}</h2>
            <p className="carousel-body">{slide.body}</p>  
            <Link to="/la_escuela" className="carousel-cta">
              ¿Por qué elegirnos? →
            </Link>
          </div>

          <div className="carousel-image-col">
            <img src={slide.image} alt={slide.imageAlt} />
          </div>
        </div>
      </div>
        
      <div className="carousel-controls">
        <button onClick={prev}>‹</button>

        <div className="carousel-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={i === current ? "active" : ""}
              onClick={() => goTo(i, i > current ? "next" : "prev")}
            />
          ))}
        </div>
        <button onClick={next}>›</button>
      </div>

      {/* Contenedor del formulario flotante permanente */}
      <div 
        ref={formRef}
        className="contenedor-formulario"
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleMouseDown}
      >
        <FormularioCursos isSticky={false} />
      </div>
    </div>
  );
}